import Link from "next/link";
import {
  BookOpen,
  Braces,
  House,
  LifeBuoy,
  LogOut,
  Monitor,
  Moon,
  Newspaper,
  Settings,
  SquareTerminal,
  Sun,
  User
} from "lucide-react";
import { useTheme } from "next-themes";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { TenantSwitcher } from "../tenant/TenantSwitcher";

const themeOptions = [
  { value: "system", icon: Monitor, label: "System theme" },
  { value: "light", icon: Sun, label: "Light theme" },
  { value: "dark", icon: Moon, label: "Dark theme" }
] as const;

export const AccountMenu = ({
  name,
  email,
  image,
  planTitle,
  isSubscribed
}: {
  name?: string | null;
  email?: string | null;
  image?: string | null;
  planTitle?: string | null;
  isSubscribed?: boolean;
}) => {
  const { theme, setTheme } = useTheme();
  // Badge colour reflects tier (Pro/Enterprise = primary); the CTA reflects whether
  // there's a paid plan to manage (any paid plan) vs a free account to upgrade.
  const isPremium = /pro|enterprise/.test(planTitle?.toLowerCase() ?? "");

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="overflow-hidden rounded-full h-8 w-8">
          {image ? (
            <img
              src={image}
              alt="Profile"
              className="inset-0 object-cover h-8 w-8 rounded-full"
            />
          ) : (
            <User />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 [&_svg]:stroke-[1.5]">
        <DropdownMenuItem asChild className="h-auto py-2">
          <Link href="/settings/general">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium text-foreground">
                  {name ?? email}
                </span>
                {planTitle && (
                  <span
                    className={cn(
                      "shrink-0 rounded-md border-[0.6px] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                      isPremium
                        ? "border-primary text-primary"
                        : "border-muted-foreground text-muted-foreground"
                    )}
                  >
                    {planTitle}
                  </span>
                )}
              </span>
              <span className="truncate text-xs text-muted-foreground">{email}</span>
            </div>
            <Settings className="ml-auto" />
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-sm">Theme</span>
          <div className="flex items-center gap-0.5 rounded-full border border-border p-0.5">
            {themeOptions.map(({ value, icon: ThemeIcon, label }) => (
              <button
                key={value}
                type="button"
                aria-label={label}
                aria-pressed={theme === value}
                onClick={() => setTheme(value)}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                  theme === value
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <ThemeIcon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
        <DropdownMenuSeparator />

        <TenantSwitcher />

        <DropdownMenuItem asChild>
          <Link href="https://docs.dollardeploy.com">
            <BookOpen />
            Documentation
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/apidocs">
            <Braces />
            API docs
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="https://docs.dollardeploy.com/cli">
            <SquareTerminal />
            CLI docs
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/#stay">
            <House />
            Home
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/blog">
            <Newspaper />
            Blog
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="mailto:ruslan@wizecore.com">
            <LifeBuoy />
            Support
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" asChild>
          <Link href="/auth/signOut">
            <LogOut />
            Log out
          </Link>
        </DropdownMenuItem>

        <div className="p-1 pt-1.5">
          <DropdownMenuItem
            asChild
            className={cn(
              "relative h-9 justify-center overflow-hidden rounded-md font-medium transition-[filter,transform] active:scale-[0.98]",
              isSubscribed
                ? "border border-border"
                : "text-primary-foreground hover:brightness-110 focus:text-primary-foreground focus:brightness-110"
            )}
            style={
              isSubscribed
                ? undefined
                : {
                    background:
                      "linear-gradient(to bottom, color-mix(in oklch, white 14%, var(--primary)), var(--primary))"
                  }
            }
          >
            <Link href={isSubscribed ? "/settings/subscription" : "/pricing"}>
              {!isSubscribed && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-linear-to-r from-transparent via-white/20 to-transparent motion-safe:animate-shimmer"
                />
              )}
              {isSubscribed ? "Manage plan" : "Upgrade to Pro"}
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
